import { includes, isEmpty, isPlainObject } from "lodash";
import invariant from "invariant";

export class ApiError extends Error {
  status: number;
  responseJson?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    responseJson?: Record<string, unknown>,
  ) {
    super(message);
    this.status = status;
    this.responseJson = responseJson;
  }
}

// react-query `retry` predicate: skip retries on 4xx responses (the server
// won't change its mind on auth/permission/validation errors), but keep
// retrying on network failures and 5xx.
export const retryUnlessClientError = (_: number, error: unknown) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500);

export const csrfTokenHeader = (): Record<string, string> => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  const token = meta?.getAttribute("content");
  return token ? { "X-CSRF-Token": token } : {};
};

export const buildHeaders = ({
  method,
  body,
  headers,
  isFormData = false,
}: {
  method: string;
  body: any;
  headers: object;
  isFormData?: boolean;
}): Record<string, string> => {
  let h: Record<string, string> = {
    Accept: "application/json",
    "Cache-Control": "no-store",
  };

  if (includes(["POST", "PUT", "DELETE", "PATCH"], method)) {
    h = { ...h, ...csrfTokenHeader() };
  }

  // Don't set Content-Type for FormData - let the browser set it with the boundary
  if (!isEmpty(body) && !isFormData) {
    h["Content-Type"] = "application/json";
  }

  if (isPlainObject(headers)) {
    return h;
  } else {
    return { ...h, ...headers };
  }
};

export const okJson = async (response: {
  status: number;
  ok: boolean;
  json: () => any;
}) => {
  if (response.status === 204) return null;

  if (response.ok) {
    return response.json();
  } else {
    let errorDetails: Record<string, unknown> | null;
    try {
      errorDetails = await response.json();
    } catch {
      errorDetails = null;
    }

    throw new ApiError(
      (errorDetails?.message as string) || I18n.t("js.generic_error"),
      response.status,
      errorDetails ?? undefined,
    );
  }
};

const parseLinkHeader = (linkHeader: string | null): string | undefined => {
  if (!linkHeader) return undefined;

  const match = linkHeader.match(/<([^>]+)>;\s*rel="next"/);
  return match ? match[1] : undefined;
};

export async function fetchApi(
  url: string,
  options:
    | {
        method?: string;
        body?: any;
        headers?: object;
        parsePaginationHeaders?: boolean;
        signal?: AbortSignal;
        parseLinkHeaders?: boolean;
      }
    | undefined = {},
) {
  invariant(!url.includes("Promise"), "url must be an absolute url");

  const { method = "GET", body, headers = {} } = options;
  const isFormData = body instanceof FormData;

  const response = await fetch(url, {
    method,
    headers: buildHeaders({ method, body, headers, isFormData }),
    credentials: "include",
    body: body ? (isFormData ? body : JSON.stringify(body)) : null,
    signal: options.signal,
  });

  // Broadcast request completion for any interested listener. `fetch` has no
  // global completion hook (unlike jQuery's `ajaxComplete`), so emitting from
  // this central wrapper is the only way observers can see fetch traffic.
  // Currently consumed by SessionWatchdog.tsx to (re-)evaluate the session.
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("tixxt:request-complete", {
        detail: { status: response.status, url },
      }),
    );
  }

  const payload = await okJson(response);

  if (options.parseLinkHeaders) {
    const linkHeader = response.headers.get("Link");
    const nextPageUrl = parseLinkHeader(linkHeader);

    return {
      data: payload,
      nextPageUrl,
    };
  }

  if (options.parsePaginationHeaders) {
    return {
      payload,
      pagination: {
        nextPage: response.headers.get("X-Next-Page")
          ? Number(response.headers.get("X-Next-Page"))
          : null,
        order: response.headers.get("X-Order") || null,
        page: response.headers.get("X-Page")
          ? Number(response.headers.get("X-Page"))
          : null,
        perPage: response.headers.get("X-Per-Page")
          ? Number(response.headers.get("X-Per-Page"))
          : null,
        prevPage: response.headers.get("X-Prev-Page")
          ? Number(response.headers.get("X-Prev-Page"))
          : null,
        sortBy: response.headers.get("X-Sort-By") || null,
        total: response.headers.get("X-Total")
          ? Number(response.headers.get("X-Total"))
          : null,
        totalArchived: response.headers.get("X-Total-Archived")
          ? Number(response.headers.get("X-Total-Archived"))
          : null,
        totalPages: response.headers.get("X-Total-Pages")
          ? Number(response.headers.get("X-Total-Pages"))
          : null,
      },
    };
  } else {
    return payload;
  }
}
